import React, { useRef, useState } from "react";
import { Formik } from "formik";
import { TextField, withStyles, Button, Typography, MenuItem, FormControl, InputLabel, CircularProgress, Select } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { renderToolTip, renderToolTipNamespace } from '../../sc-utilities/accountUtilities'
import * as Yup from "yup";
import * as momentTimezone from "moment-timezone";
import { DropzoneArea } from 'material-ui-dropzone';
import { passwordComplexity } from '../../sc-utilities/CustomerUtilities';
import { hasPermission, capitalize } from "../../sc-utilities";
import config from '../../../config/config';
import LicensingWarningModal from "./Details/LicensingWarningModal";

const styles = theme => ({
    root: {
        width: "auto",
        height: "auto",
        padding: theme.spacing(2),
        margin: theme.spacing(2),
    },
    rootEdit: {
        flexGrow: 1,
        paddingTop: theme.spacing(2),
    },
    submit: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        backgroundColor: theme.palette.primary.main,
        borderRadius: '1px',
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        },
        width: "120px",
        marginTop: "10px"
    },
    submitEdit: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        },
        width: "100%",
    },
    cancel: {
        width: "120px",
        marginTop: "10px",
        marginRight: "10px",
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.buttonText.default
        }
    },
    formControl: {
        width: "100%"
    },
    formControlInfo: {
        width: "100%",
        display: 'flex',
        flexDirection: 'row'
    },
    namespace: {
        width: "98%"
    },
    input: {
        display: 'none'
    },
    buttons: {
        textAlign: "right"
    },
    info: {
        position: 'relative',
        marginTop: "38px",
        marginLeft: "15px"
    },
    infoControl: {
        position: 'relative',
        marginTop: "38px",
        marginLeft: "15px"
    },
    imgDiv: {
        width: '100%',
        height: 'auto',
        textAlign: 'center',
        justifyContent: 'center'
    },
    img: {
        width: '100%',
        height: '100%',
        maxWidth: window.innerWidth - 100,
        maxHeight: window.innerHeight - 100
    },
    buttonChoose: {
        marginTop: 20,
        width: '100%'
    }
});

const CustomerForm = props => {
    const { classes,
        loading,
        initialValues,
        handleSubmit,
        edit,
        formSettings,
        translations,
        handleNext,
        handleUpload,
        backValid,
        backendError,
        permissions,
        setBackendError,
        countryList,
        usStatesList
    } = props;

    let submitAction = ''
    const key = 'customer';

    const timezones = momentTimezone.tz.names().map((tz) => {
        const offset = momentTimezone.tz(tz).format('Z'); // e.g. +05:30
        return {
            name: tz,
            label: `${tz} (UTC${offset})`
        };
    });

    // Add a custom alias for India Standard Time
    timezones.push({
        name: 'Asia/Kolkata',
        label: 'India Standard Time (Asia/Kolkata, UTC+05:30)'
    });
    
    timezones.sort((tz1, tz2) => tz1.label.localeCompare(tz2.label));

    const formRef = useRef()

    const licensingSettingOptions = [
        { value: "default_off", displayValue: "Default off" },
        { value: "default_on", displayValue: "Default on" },
        { value: "always_off", displayValue: "Always off" },
        { value: "always_on", displayValue: "Always on" },
    ];

    const [licensingModal, setLicensingModal] = useState({ open: false });

    const closeLicensingModal = () => {
        setLicensingModal({ open: false });
    }

    const changeLicensingSelect = (fieldName, value) => {
        if (formRef.current) {
            formRef.current.setFieldValue(fieldName, value);
        }
        closeLicensingModal();
    }

    const getLicensingSettingOptions = () => {
        return licensingSettingOptions.map((option) => {
            return (
                <MenuItem key={option.value} value={option.value}>
                    {translations[option.value] || option.displayValue}
                </MenuItem>
            )
        })
    }

    return (
        <div className={classes.root}>
            <Formik
                innerRef={formRef}
                initialValues={initialValues}
                enableReinitialize
                onSubmit={async (values, { setSubmitting }) => {
                    if (values?.Country?.key !== 'US') {
                        delete values.State;
                    }
                    if (submitAction === 'finish') {

                        setSubmitting(true);
                        await handleSubmit(values);
                        setSubmitting(false);
                    }
                }}
                validationSchema={Yup.object().shape(formSettings.Validation)}
            >
                {formikProps => {
                    const { values, touched, errors, isValid, dirty, isSubmitting, handleChange, handleBlur, handleSubmit, setFieldValue, setValues } = formikProps;

                    const handleLicensingChange = (fieldName, value) => {
                        if (["always_off", "always_on"].includes(value)) {
                            setLicensingModal({ open: true, fieldName, value });
                        } else {
                            setFieldValue(fieldName, value);
                        }
                    }

                    const onChangeNamespace = (value) => {
                        if (backendError && backendError === "Namespace") setBackendError()
                        setFieldValue("Namespace", value)
                    }

                    const inputProps = {
                        autoComplete: 'new-password',
                        form: {
                            autoComplete: 'off',
                        },
                    }

                    return (
                        <form onSubmit={handleSubmit}>
                            <Typography variant={"h6"}>{formSettings.FormTitle ? formSettings.FormTitle : null}</Typography>
                            <TextField
                                name="Name"
                                value={values.Name || ''}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.Name && touched.Name)}
                                placeholder={translations["name"] || "Name"}
                                label={translations["name"] || "Name"}
                                margin={"dense"}
                                fullWidth={true}
                                inputProps={inputProps}
                            />
                            <FormControl className={classes.formControlInfo} margin={"dense"}>
                                <TextField
                                    name="Namespace"
                                    value={values.Namespace}
                                    onChange={(event) => onChangeNamespace(event.target.value)}
                                    onBlur={handleBlur}
                                    disabled={edit}
                                    error={!!(errors.Namespace && touched.Namespace) || (backendError && backendError === "Namespace")}
                                    placeholder={translations["namespace"] || "Namespace"}
                                    label={translations["namespace"] || "Namespace"}
                                    margin={"dense"}
                                    className={classes.namespace}
                                    inputProps={inputProps}
                                />
                                {renderToolTipNamespace(translations, classes.info)}
                            </FormControl>

                            <Autocomplete
                                name="Country"
                                margin={"dense"}
                                options={countryList || []}

                                filterSelectedOptions
                                value={values.Country}

                                onChange={(_event, newValue) => {
                                    if (newValue?.key !== 'US') {
                                        setValues({ ...values, 'Country': newValue, 'State': '' });
                                    } else {
                                        setValues({ ...values, 'Country': newValue, 'State': null });
                                    }
                                }}
                                getOptionLabel={(option) => option.value}
                                renderInput={params => <TextField {...params} label={translations['country'] || 'Country'} margin="dense" fullWidth={true} />}
                            />

                            {(values?.Country && values?.Country['key'] === 'US') || values.State ?
                                <Autocomplete
                                    name="State"
                                    margin={"dense"}
                                    options={usStatesList ? [...usStatesList] : []}
                                    value={values?.State || ''}
                                    onChange={(_event, newValue) => {
                                        setFieldValue('State', newValue);
                                    }}
                                    getOptionLabel={option => option?.value || ''}
                                    renderInput={params => <TextField {...params} label={translations['state'] || 'State'} margin="dense" fullWidth={true} />}
                                /> : null}

                            <TextField
                                name="City"
                                value={values?.City || (values.Address ? JSON.parse(values?.Address)?.City : '')}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.City && touched.City)}
                                placeholder={translations["city"] || "City"}
                                label={translations["city"] || "City"}
                                margin={"dense"}
                                fullWidth={true}
                                inputProps={inputProps}
                            />

                            <TextField
                                name="AddressLine1"
                                value={values?.AddressLine1 || (values.Address ? JSON.parse(values?.Address)?.AddressLine1 : '')}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.AddressLine1 && touched.AddressLine1)}
                                placeholder={translations["address_line1"] || "Address Line1"}
                                label={translations["address_line1"] || "Address Line1"}
                                margin={"dense"}
                                fullWidth={true}
                                inputProps={inputProps}
                            />

                            <TextField
                                name="AddressLine2"
                                value={values?.AddressLine2 || (values.Address ? JSON.parse(values?.Address)?.AddressLine2 : '')}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.AddressLine2 && touched.AddressLine2)}
                                placeholder={translations["address_line2"] || "Address Line2"}
                                label={translations["address_line2"] || "Address Line2"}
                                margin={"dense"}
                                fullWidth={true}
                                inputProps={inputProps}
                            />

                            <TextField
                                name="AutoLogout"
                                type='number'
                                value={values?.AutoLogout || 0}
                                InputProps={{
                                    inputProps: {
                                        min: 0
                                    },
                                    autoComplete: 'new-password',
                                    form: {
                                        autoComplete: 'off',
                                    },
                                }}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.AutoLogout && touched.AutoLogout)}
                                placeholder={translations["auto_logout"] || "Auto Logout"}
                                label={translations["auto_logout"] || "Auto Logout"}
                                margin={"dense"}
                                fullWidth={true}
                            />

                            <Autocomplete
                                name={'Timezone'}
                                margin={"dense"}
                                options={timezones ? [...timezones] : []}
                                value={typeof values.Timezone === 'string' ? timezones.find((tz) => tz?.name === values.Timezone)?.label || values.Timezone : values.Timezone}
                                onChange={(_event, newValue) => {
                                    setFieldValue('Timezone', newValue?.name);
                                }}
                                getOptionLabel={option => ((option && typeof option === 'object' && !Array.isArray(option) && option.hasOwnProperty('label')) ? option.label : typeof option === 'string' ? option : config?.timezone)}
                                renderInput={params => <TextField {...params} label={translations['timezone'] || 'Timezone'} margin="dense" fullWidth={true} />}
                            />
                            <FormControl className={classes.formControl} margin={"dense"}>
                                <InputLabel shrink>{translations['select_status'] || "Please select customer status"}</InputLabel>
                                <Select
                                    disabled={!hasPermission(permissions, "access_all_customers")}
                                    name="Status"
                                    label={translations["status"] || "Status"}
                                    margin={"dense"}
                                    value={values?.Status || 'active'}
                                    onChange={handleChange}
                                >
                                    <MenuItem key={'active'} value={'active'}>
                                        {translations["active"] || `Active`}
                                    </MenuItem>
                                    <MenuItem key={'inactive'} value={'inactive'}>
                                        {translations["inactive"] || 'Inactive'}
                                    </MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl className={classes.formControlInfo} margin={"dense"}>
                                <InputLabel shrink>{translations['select_password_complexity'] || "Please select password complexity"}</InputLabel>                                <Select
                                    name="PasswordComplexity"
                                    label={translations['password_complexity'] || 'Password complexity'}
                                    margin={"dense"}
                                    className={classes.namespace}
                                    value={values.PasswordComplexity || 'securaze_basic'}
                                    onChange={handleChange}
                                >
                                    {(passwordComplexity ? passwordComplexity : []).map((el) => {
                                        return (<MenuItem key={el.value} value={el.value}>
                                            {translations[el.translate] || el.label}
                                        </MenuItem>)
                                    })}
                                </Select>
                                {renderToolTip(values.PasswordComplexity, translations, classes.infoControl)}
                            </FormControl>

                            <FormControl className={classes.formControl} margin={"dense"}>
                                <InputLabel shrink>{translations['select_individual_licensing_setting'] || "Please select individual licensing setting"}</InputLabel>
                                <Select
                                    name="IndividualLicensingSetting"
                                    label={translations["individual_licensing_setting"] || "Individual licensing setting"}
                                    margin={"dense"}
                                    value={values?.IndividualLicensingSetting || ''}
                                    onChange={(e) => handleLicensingChange("IndividualLicensingSetting", e.target.value)}
                                >
                                    {getLicensingSettingOptions()}
                                </Select>
                            </FormControl>

                            <FormControl className={classes.formControl} margin={"dense"}>
                                <InputLabel shrink>{translations['select_group_licensing_setting'] || "Please select group licensing setting"}</InputLabel>
                                <Select
                                    name="GroupLicensingSetting"
                                    label={translations["group_licensing_setting"] || "Group licensing setting"}
                                    margin={"dense"}
                                    value={values?.GroupLicensingSetting || ''}
                                    onChange={(e) => handleLicensingChange("GroupLicensingSetting", e.target.value)}
                                >
                                    {getLicensingSettingOptions()}
                                </Select>
                            </FormControl>

                            <FormControl className={classes.formControl} margin={"dense"}>
                                <InputLabel shrink>{translations["select_unique_inventory_number"] || "Please select if Inventory numbers should be unique"}</InputLabel>
                                <Select
                                    name="UniqueInventoryNumberEnabled"
                                    value={values.UniqueInventoryNumberEnabled}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={!!(errors.UniqueInventoryNumberEnabled && touched.UniqueInventoryNumberEnabled)}
                                    placeholder={translations["unique_inventory_number_enabled"] || "UniqueInventoryNumberEnabled"}
                                    label={translations["unique_inventory_number_enabled"] || "UniqueInventoryNumberEnabled"}
                                    margin={"dense"}
                                    fullWidth={true}
                                >
                                    <MenuItem value={true}>
                                        {translations["yes"] || "Yes"}
                                    </MenuItem>
                                    <MenuItem value={false}>
                                        {translations["no"] || "No"}
                                    </MenuItem>
                                </Select>
                            </FormControl>
                            {!edit ?
                                <>
                                    <FormControl className={classes.formControl} margin={"dense"}>
                                        <InputLabel shrink>{translations['default_customer_data'] || "Create default customer data"}</InputLabel>
                                        <Select
                                            name="CreateDefaultData"
                                            label={translations['default_customer_data'] || "Create default customer data"}
                                            margin={"dense"}
                                            value={values?.CreateDefaultData || 'yes'}
                                            onChange={handleChange}
                                        >
                                            <MenuItem key={'yes'} value={'yes'}>
                                                {translations["yes"] || `Yes`}
                                            </MenuItem>
                                            <MenuItem key={'no'} value={'no'}>
                                                {translations["no"] || 'No'}
                                            </MenuItem>
                                        </Select>
                                    </FormControl>

                                    <div className={classes.imgDiv}>
                                        <DropzoneArea
                                            filesLimit={1}
                                            maxFileSize={5368709120}
                                            getFileRemovedMessage={(fileName) => {
                                                const text = translations ? (
                                                    capitalize(`${translations['file'] || 'File'} ${fileName} ${translations['removed'] || 'removed'}.`)
                                                ) : `File ${fileName} removed.`

                                                return text;
                                            }}
                                            getFileAddedMessage={(fileName) => {
                                                const text = translations ? (
                                                    capitalize(`${translations['file'] || 'File'} ${fileName} ${translations['successfully_added'] || 'successfully added'}.`)
                                                ) : `File ${fileName} successfully added.`

                                                return text;
                                            }}
                                            getFileLimitExceedMessage={(filesLimit) => {
                                                const text = translations ? (
                                                    capitalize(`${translations['maximum_allowed_number'] || 'Maximum allowed number of files exceeded. Only'} ${filesLimit} ${translations['allowed'] || 'allowed'}.`)
                                                ) : `Maximum allowed number of files exceeded. Only ${filesLimit} allowed`

                                                return text;
                                            }}
                                            getDropRejectMessage={(rejectedFile) => {
                                                const text = translations ? (
                                                    capitalize(`${translations['File'] || 'File'} ${translations['was_rejected'] || 'was rejected'}.`)
                                                ) : `File was rejected.`

                                                return text;
                                            }}
                                            dropzoneText={translations['drag_and_drop'] || 'Drag and drop an image file here or click'}
                                            onChange={handleUpload}
                                            acceptedFiles={['image/jpg', 'image/jpeg', 'image/png']}
                                        />

                                    </div>
                                </> : null}
                            <div className={classes.buttons}>
                                {formSettings.ButtonCancel ? (
                                    <Button
                                        className={classes.cancel}
                                        variant="contained"
                                        color={formSettings.ButtonCancel.Color}
                                        onClick={formSettings.ButtonCancel.handleCancel}
                                        disabled={loading}
                                    >
                                        {formSettings.ButtonCancel.Title}
                                    </Button>
                                ) : null
                                }
                                {formSettings.ButtonNext ? (
                                    <Button
                                        className={classes.cancel}
                                        variant="contained"
                                        onClick={() => handleNext(values, key)}
                                        color={formSettings.ButtonNext.Color}
                                        disabled={backValid && backValid[key] ? !backValid[key] : loading || !(isValid && dirty) || isSubmitting}
                                    >
                                        {formSettings.ButtonNext.Title}
                                    </Button>
                                ) : null
                                }
                                {formSettings.ButtonSubmit ? (

                                    < Button
                                        className={edit ? classes.submitEdit : classes.submit}
                                        variant="contained"
                                        color={formSettings.ButtonSubmit.Color}
                                        type="submit"
                                        onClick={() => {
                                            submitAction = 'finish';
                                        }}
                                        disabled={
                                            backValid && backValid[key] ? !backValid[key] : loading || !(isValid && dirty) || isSubmitting || loading
                                        }
                                    >
                                        {loading || isSubmitting ? <CircularProgress size={24} /> : formSettings.ButtonSubmit.Title}
                                    </Button>
                                ) : null}
                            </div>
                        </form>
                    );
                }}
            </Formik>
            <LicensingWarningModal translations={translations} modalState={licensingModal} handleCloseSafe={closeLicensingModal} handleCloseDanger={changeLicensingSelect} />
        </div >
    );
};


export default withStyles(styles, { withTheme: true })(CustomerForm);
